import { type ChangeEvent, type DragEvent, useEffect, useId, useRef, useState } from 'react'
import { invitation } from '../data/invitation.js'
import { useModalDialog } from '../hooks/useModalDialog.js'
import { PREVIEW_LIMIT, isUploadable, summarize, uploadSnaps } from '../lib/upload.js'
import styles from './SnapModal.module.css'

export interface SnapModalProps {
  onClose: () => void
  onNotify: (message: string) => void
}

interface PreviewEntry {
  /** 사진은 즉시(URL.createObjectURL), 동영상은 loadeddata 이후 캔버스 프레임이 준비되면 채워진다. */
  src: string | null
}

const VIDEO_EXT = /\.(mp4|mov|m4v|avi|mkv|webm|3gp)$/i

function isVideoFile(file: File): boolean {
  if (file.type.toLowerCase().startsWith('video/')) return true
  return VIDEO_EXT.test(file.name)
}

function mapUploadError(error: unknown): string {
  const message = error instanceof Error ? error.message : ''
  switch (message) {
    case 'not_open_yet':
      return '예식 시작부터 올릴 수 있어요'
    case 'insufficient_storage':
      return '지금은 사진을 받을 수 없어요'
    case 'too_many_requests':
      return '잠시 후 다시 시도해 주세요'
    case 'aborted':
      return '업로드를 취소했어요'
    case 'timeout':
      return '연결이 멈춰 업로드를 중단했어요'
    default:
      return '사진을 보내지 못했어요'
  }
}

/** 원본 snapOpen 오버레이. */
export function SnapModal({ onClose, onNotify }: SnapModalProps) {
  const inputId = useId()
  const [files, setFiles] = useState<File[]>([])
  const [previews, setPreviews] = useState<PreviewEntry[]>([])
  const [sending, setSending] = useState(false)
  const [progress, setProgress] = useState(0)
  const mountedRef = useRef(true)
  const abortRef = useRef<AbortController | null>(null)

  useEffect(
    () => () => {
      mountedRef.current = false
      // 열린 채 부모가 사라져도 업로드가 유령처럼 남지 않는다.
      abortRef.current?.abort()
    },
    [],
  )

  // 미리보기는 앞 PREVIEW_LIMIT장만 만든다 — 사진은 objectURL을 곧장 쓰고,
  // 동영상은 <video>를 오프스크린에 붙여 첫 프레임을 캔버스로 그린 뒤
  // dataURL로 바꾼다. cleanup은 files가 바뀔 때(다시 골랐을 때)와
  // 언마운트될 때 둘 다 실행되므로 — 만든 objectURL이 새는 경우가 없다.
  useEffect(() => {
    const subset = files.slice(0, PREVIEW_LIMIT)
    const createdUrls: string[] = []
    let cancelled = false

    const initial: PreviewEntry[] = subset.map((file) => {
      if (isVideoFile(file)) return { src: null }
      const url = URL.createObjectURL(file)
      createdUrls.push(url)
      return { src: url }
    })
    setPreviews(initial)

    subset.forEach((file, index) => {
      if (!isVideoFile(file)) return

      const videoUrl = URL.createObjectURL(file)
      createdUrls.push(videoUrl)

      const video = document.createElement('video')
      video.muted = true
      video.playsInline = true
      video.src = videoUrl

      const handleLoadedData = () => {
        if (cancelled) return
        const canvas = document.createElement('canvas')
        canvas.width = video.videoWidth || 1
        canvas.height = video.videoHeight || 1
        const ctx = canvas.getContext('2d')
        if (!ctx) return
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height)
        const dataUrl = canvas.toDataURL('image/jpeg', 0.82)
        setPreviews((current) => {
          const next = [...current]
          const target = next[index]
          if (target) next[index] = { ...target, src: dataUrl }
          return next
        })
      }
      video.addEventListener('loadeddata', handleLoadedData, { once: true })
    })

    return () => {
      cancelled = true
      for (const url of createdUrls) URL.revokeObjectURL(url)
    }
  }, [files])

  function addFiles(list: FileList | File[]) {
    const incoming = Array.from(list).filter(isUploadable)
    if (incoming.length === 0) return
    setFiles((current) => [...current, ...incoming])
  }

  function handlePick(event: ChangeEvent<HTMLInputElement>) {
    if (event.target.files) addFiles(event.target.files)
    event.target.value = ''
  }

  function handleDragOver(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
  }

  function handleDrop(event: DragEvent<HTMLLabelElement>) {
    event.preventDefault()
    if (sending) return
    if (event.dataTransfer.files) addFiles(event.dataTransfer.files)
  }

  async function handleSend() {
    if (files.length === 0) {
      onNotify('먼저 사진을 선택해 주세요')
      return
    }
    const controller = new AbortController()
    abortRef.current = controller
    setSending(true)
    setProgress(0)
    try {
      const result = await uploadSnaps(
        files,
        (fraction) => {
          if (mountedRef.current) setProgress(fraction)
        },
        { signal: controller.signal },
      )
      abortRef.current = null
      onNotify(summarize(result))
      onClose()
      return
    } catch (error) {
      abortRef.current = null
      onNotify(mapUploadError(error))
    }
    if (mountedRef.current) setSending(false)
  }

  /** 보내는 중에도 빠져나갈 문을 남긴다 — 혼잡한 데이터의 300MB 영상은 하객을 가둘 수 있다. */
  function handleCancel() {
    abortRef.current?.abort()
  }

  function handleClose() {
    if (sending) return
    onClose()
  }

  // 배경 스크롤 잠금·Escape·초점 관리를 라이트박스와 같은 규칙으로 공유한다.
  const dialogRef = useModalDialog(handleClose)

  const remainder = Math.max(0, files.length - PREVIEW_LIMIT)
  const percent = Math.round(Math.min(1, Math.max(0, progress)) * 100)

  return (
    <div
      ref={dialogRef}
      tabIndex={-1}
      className={styles.backdrop}
      role="dialog"
      aria-modal="true"
      aria-label="게스트 스냅 업로드"
      onClick={handleClose}
    >
      <div className={styles.panel} onClick={(event) => event.stopPropagation()}>
        <div className={styles.title}>게스트 스냅 업로드</div>
        <div className={styles.subtitle}>
          {invitation.couple.groom.name} · {invitation.couple.bride.name}에게 여러분의 사진을 전해 주세요
        </div>
        <label
          htmlFor={inputId}
          className={sending ? `${styles.dropzone} ${styles.dropzoneDisabled}` : styles.dropzone}
          onDragOver={handleDragOver}
          onDrop={handleDrop}
        >
          <span className={styles.dropzoneText}>
            여기를 눌러 사진 선택
            <br />
            또는 사진을 끌어다 놓기
          </span>
          <input
            id={inputId}
            className={styles.input}
            type="file"
            multiple
            accept="image/*,video/*"
            disabled={sending}
            onChange={handlePick}
          />
        </label>
        {files.length > 0 && <div className={styles.count}>{files.length}장 선택됨</div>}
        {previews.length > 0 && (
          <div className={styles.grid}>
            {previews.map((preview, index) => (
              <div key={index} className={styles.tile}>
                {preview.src !== null && <img className={styles.thumb} src={preview.src} alt="선택한 사진 미리보기" />}
              </div>
            ))}
          </div>
        )}
        {remainder > 0 && <div className={styles.remainder}>외 {remainder}장</div>}
        {sending && (
          <div className={styles.progress}>
            <div
              className={styles.progressTrack}
              role="progressbar"
              aria-label="업로드 진행률"
              aria-valuemin={0}
              aria-valuemax={100}
              aria-valuenow={percent}
            >
              <div className={styles.progressFill} style={{ width: `${percent}%` }} />
            </div>
            <div className={styles.progressLabel}>{percent}%</div>
          </div>
        )}
        <div className={styles.actions}>
          {sending ? (
            <button type="button" className={styles.closeBtn} onClick={handleCancel}>
              취소
            </button>
          ) : (
            <button type="button" className={styles.closeBtn} onClick={handleClose}>
              닫기
            </button>
          )}
          <button type="button" className={styles.sendBtn} disabled={sending} onClick={() => void handleSend()}>
            보내기
          </button>
        </div>
      </div>
    </div>
  )
}
